/**
 * Seção "Exemplos de resposta" em modo edição (Brief 4 · Tela 2 · PR4).
 *
 * Lista editável de pares { situation, exemplaryResponse } sobre o draft. Adiciona
 * (makeExample) e remove itens; cada par tem um input (situação) + textarea
 * (resposta exemplar). Espelha o add/remove de TabStepsEdit. R15: o backend grava
 * o ARRAY inteiro. Keying por índice (exemplos não têm id) — inputs controlados,
 * sem perda de foco. Não confundir com o bloco "Preview/Gerar resposta" do
 * gabarito (OMITIDO: sem backend de geração, seria dado falso).
 */

import { Plus, Trash2 } from 'lucide-react';

import { makeExample } from '../use-persona-form';

import type { PersonaExample } from '../types';
import type { PersonaForm } from '../use-persona-form';

export function SectionExamplesEdit({ form }: { form: PersonaForm }): JSX.Element {
  const { examples } = form.draft;

  const update = (index: number, patch: Partial<PersonaExample>): void => {
    form.set(
      'examples',
      examples.map((e, i) => (i === index ? { ...e, ...patch } : e)),
    );
  };

  const add = (): void => {
    form.set('examples', [...examples, makeExample()]);
  };

  const remove = (index: number): void => {
    form.set(
      'examples',
      examples.filter((_, i) => i !== index),
    );
  };

  return (
    <section className="pe-panel">
      <div className="pe-panel__head-row">
        <div>
          <h2 className="pe-panel__title">Exemplos de resposta</h2>
          <p className="pe-panel__subtitle">
            Respostas exemplares que ancoram o comportamento da IA.
          </p>
        </div>
        <button type="button" className="pe-btn pe-btn--sm" onClick={add}>
          <Plus size={14} /> Adicionar exemplo
        </button>
      </div>

      {examples.length > 0 ? (
        <div className="pe-examples">
          {examples.map((example, index) => (
            // exemplos não têm id; key por índice é seguro aqui (inputs controlados, re-render total)
            <article className="pe-example pe-example--edit" key={index}>
              <div className="pe-example__head">
                <span className="pe-field__label">Exemplo {index + 1}</span>
                <button
                  type="button"
                  className="pe-btn pe-btn--ghost pe-btn--sm"
                  aria-label={`Remover exemplo ${index + 1}`}
                  onClick={() => {
                    remove(index);
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="pe-field">
                <span className="pe-field__label">Situação</span>
                <input
                  type="text"
                  className="pe-input"
                  value={example.situation}
                  aria-label={`Situação do exemplo ${index + 1}`}
                  onChange={(e) => {
                    update(index, { situation: e.target.value });
                  }}
                />
              </div>
              <div className="pe-field">
                <span className="pe-field__label">Resposta exemplar</span>
                <textarea
                  className="pe-textarea"
                  rows={3}
                  value={example.exemplaryResponse}
                  aria-label={`Resposta exemplar do exemplo ${index + 1}`}
                  onChange={(e) => {
                    update(index, { exemplaryResponse: e.target.value });
                  }}
                />
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="pe-read-value pe-read-value--muted">
          Nenhum exemplo. Use “Adicionar exemplo” para criar o primeiro.
        </p>
      )}
    </section>
  );
}
